<script setup>
import { Head, Link, usePage } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';
import SocialShare from '@/Components/Front/SocialShare.vue';

const props = defineProps({
    feature: Object,
    relatedFeatures: Array,
    jenisMobils: Array,
    setting: Object, // FrontLayout usually provides this via shared props or we pass it if controller sends it
    seoData: Object,
});

const page = usePage();
const appSetting = props.setting || page.props.setting; // Fallback if setting passed as prop or available globally
</script>

<template>
    <FrontLayout :setting="appSetting" :title="seoData?.title || feature.feature_title">
        <Head>

            <meta name="description" :content="seoData?.meta_description" />
            <meta name="keywords" :content="seoData?.meta_keywords" />
            <link v-if="seoData?.canonical_url" rel="canonical" :href="seoData?.canonical_url" />
            
            <meta property="og:title" :content="seoData?.og_title" />
            <meta property="og:description" :content="seoData?.og_description" />
            <meta property="og:image" :content="seoData?.og_image" />
            <meta property="og:url" :content="seoData?.og_url" />
            <meta property="og:type" :content="seoData?.og_type || 'article'" />
            <meta property="og:site_name" content="Gotransport Banyuwangi" />
            
            <meta name="twitter:title" :content="seoData?.twitter_title" />
            <meta name="twitter:description" :content="seoData?.twitter_description" />
            <meta name="twitter:image" :content="seoData?.twitter_image" />
        </Head>

        <!-- Hero Section -->
        <PageHero 
            :title="feature.feature_title" 
            :subtitle="feature.feature_subtitle"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: 'Fitur' }
            ]"
        >
            <div v-if="feature.feature_icon" class="mt-4">
                <i :class="`${feature.feature_icon} text-5xl`"></i>
            </div>
        </PageHero>

        <!-- Feature Content Section -->
        <section class="py-8 md:py-10 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <!-- Main Content -->
                    <div class="lg:col-span-2">
                        <article class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-xl">
                            <!-- Article Image -->
                            <div v-if="feature.feature_image" class="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
                                <img :src="`/storage/features/${feature.feature_image}`"
                                    :alt="feature.feature_title" 
                                    class="w-full h-auto object-cover"
                                    loading="lazy">
                            </div>

                            <!-- Feature Badge/Header -->
                            <div class="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                                <div class="flex items-center gap-3 mb-2">
                                    <i :class="feature.feature_icon ? `${feature.feature_icon} text-2xl text-blue-600 dark:text-blue-400` : 'fa fa-star text-2xl text-yellow-500'"></i>
                                    <h2 class="text-2xl font-bold text-gray-900 dark:text-white">{{ feature.feature_title }}</h2>
                                </div>
                                <p v-if="feature.feature_subtitle" class="text-sm text-gray-600 dark:text-gray-400 ml-9">{{ feature.feature_subtitle }}</p>
                            </div>

                            <!-- Article Content -->
                            <div class="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed mb-6 prose-headings:text-gray-900 dark:prose-headings:text-white prose-strong:text-gray-900 dark:prose-strong:text-white">
                                <div v-if="feature.feature_artikel" v-html="feature.feature_artikel.replace(/<h2([^>]*)>/gi, '<p class=\'text-xl font-bold text-gray-900 dark:text-white mt-6 mb-3\'$1>').replace(/<\/h2>/gi, '</p>')"></div>
                                <div v-else class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 text-center rounded-lg">
                                    <i class="fa fa-info-circle text-blue-600 dark:text-blue-400 text-2xl mb-2"></i>
                                    <p class="text-sm text-gray-700 dark:text-gray-300">
                                        Artikel untuk fitur ini sedang dalam proses penulisan. Silakan kembali lagi nanti untuk membaca konten lengkapnya.
                                    </p>
                                </div>
                            </div>

                            <!-- Feature Info & Share -->
                            <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div class="flex items-start gap-2 mb-4">
                                    <i class="fa fa-info-circle text-blue-600 dark:text-blue-400 mt-0.5"></i>
                                    <p class="text-sm text-gray-600 dark:text-gray-400">
                                        Fitur ini merupakan bagian dari layanan {{ appSetting?.setting_title || 'Gotransport' }} untuk memberikan pengalaman terbaik bagi para pelanggan.
                                    </p>
                                </div>
                                
                                <SocialShare
                                    :url="seoData?.canonical_url || page.props.ziggy.location"
                                    :title="feature.feature_title"
                                    layout="card"
                                />
                            </div>
                        </article>
                    </div>

                    <!-- Sidebar -->
                    <div class="lg:col-span-1">
                        <aside class="space-y-6">
                            <!-- Related Fitur -->
                            <div v-if="relatedFeatures && relatedFeatures.length > 0" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-xl">
                                <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                    <i class="fa fa-list-ul text-gray-600 dark:text-gray-400"></i>
                                    Fitur Lainnya
                                </h3>
                                <div class="space-y-2">
                                    <Link v-for="related in relatedFeatures" :key="related.id" :href="`/feature/${related.feature_slug}`" 
                                        class="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors group rounded-lg">
                                        <i :class="`${related.feature_icon || 'fa fa-check'} text-xl text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300`"></i>
                                        <span class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                            {{ related.feature_title }}
                                        </span>
                                    </Link>
                                </div>
                            </div>

                            <!-- Articles Section Widget -->
                            <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-xl">
                                <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                    <i class="fa fa-newspaper-o text-blue-600 dark:text-blue-400"></i>
                                    Cari Artikel?
                                </h3>
                                <p class="text-sm text-gray-700 dark:text-gray-300 mb-4">
                                    Halaman ini menampilkan fitur layanan kami. Untuk membaca artikel dan berita terbaru, silakan kunjungi:
                                </p>
                                <Link href="/news" 
                                    class="block w-full text-center px-4 py-2 border-2 border-blue-600 text-blue-600 dark:text-blue-400 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white font-medium transition-colors rounded-lg flex items-center justify-center gap-2">
                                    <i class="fa fa-arrow-right"></i>
                                    Lihat Semua Artikel
                                </Link>
                            </div>

                            <!-- Jenis Mobil Widget -->
                            <div v-if="jenisMobils && jenisMobils.length > 0" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-xl">
                                <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                    <i class="fa fa-car text-gray-600 dark:text-gray-400"></i>
                                    Kategori Armada
                                </h3>
                                <div class="space-y-2">
                                    <div v-for="jenis in jenisMobils" :key="jenis.id" class="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                                        <Link :href="`/mobil/jenis/${jenis.jenis_slug}`"
                                            class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                            <i class="fa fa-car text-gray-400"></i>
                                            {{ jenis.jenis_nama }}
                                        </Link>
                                        <span class="text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">{{ jenis.mobils_count }}</span>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>
